import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { RoofFramingComponent } from "./roofFramingComponentTypes";
import type { RoofTrussBase, RoofTrussType } from "./roofTrussTypes";

export enum RoofFramingType {
  RAFTER = "RAFTER",
  TRUSS = "TRUSS",
}

export interface RoofFramingBase<T extends RoofFramingType> {
  readonly framingType: T;
  readonly components: RoofFramingComponent[];
  readonly plane: Plane; //the plane of the framing - note the individual components may have different normals for their extrusion but they all lie on this plane
}

export type RoofRafter = RoofFramingBase<RoofFramingType.RAFTER>;

export type RoofTruss = RoofFramingBase<RoofFramingType.TRUSS> & RoofTrussBase<RoofTrussType>;

export type RoofFraming = RoofRafter | RoofTruss;

export interface GeneratedRoofFraming extends BuildingEntityBase<"ROOF_FRAMING"> {
  readonly roofSystemId: string;
  readonly levelId: string;
  readonly framing: RoofFraming[];
  readonly clippedRoofTopFacesXY: PolyLine2[];
}

export enum RoofFramingSpacingMethod {
  EQUAL_SPACING_BEGIN_END = "EQUAL_SPACING_BEGIN_END",
  EQUAL_SPACING_BEGIN = "EQUAL_SPACING_BEGIN",
  EQUAL_SPACING_END = "EQUAL_SPACING_END",
}

export enum RoofRegion {
  HIP = "HIP",
  FLYOUT = "FLYOUT",
  PEAK = "PEAK",
}

export enum HipFramingMethod {
  JACK_RAFTERS = "JACK_RAFTERS", //Uses a full-length hip rafter with jack rafters that "step down" to the corners.
  //Uses a full-length hip rafter with jack rafters that "step down" to the corners.
  STEP_DOWN_HIP_FRAMING = "STEP_DOWN_HIP_FRAMING", //this is a traditional conventional hip framing method
  //May incorporate prefabricated trusses and modified framing to suit regional snow/wind loads.
  //MIDWEST_HIP_FRAMING = "MIDWEST_HIP_FRAMING", //a popular methods in midwest and southern states
  //Involves framing over a common truss system; hips and jacks are added on top of a flat or common truss deck.
  //CALIFORNIA_HIP_FRAMING = "CALIFORNIA_HIP_FRAMING", //Layover Hip Framing
  //@TODO: add more methods
}

export interface RoofCriticalPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly distance: number;
  readonly region: RoofRegion | "NONE";
  readonly constraintType?: FramingPlaneConstraintType;
}

export enum RoofFramingPlaneType {
  FLY_RAFTER_PLANE_AT_START = "FLY_RAFTER_PLANE_AT_START",
  FLY_RAFTER_PLANE_AT_INTERIM = "FLY_RAFTER_PLANE_AT_INTERIM",
  FLY_RAFTER_PLANE_AT_END = "FLY_RAFTER_PLANE_AT_END",
  STICK_FRAME_PLANE_AT_START = "STICK_FRAME_PLANE_AT_START",
  STICK_FRAME_PLANE_INTERIM = "STICK_FRAME_PLANE_INTERIM",
  STICK_FRAME_PLANE_AT_END = "STICK_FRAME_PLANE_AT_END",
  HIP_FRAMING_PLANE = "HIP_FRAMING_PLANE",
  PEAK_FRAMING_PLANE = "PEAK_FRAMING_PLANE",
  NO_FRAMING = "NO_FRAMING",
}

export interface GeneratedRoofFramingPlane {
  readonly plane: Plane;
  readonly type: RoofFramingPlaneType;
}

export enum FramingPlaneConstraintType {
  START_STEP_DOWN_FRAMING = "START_STEP_DOWN_FRAMING",
  END_STEP_DOWN_FRAMING = "END_STEP_DOWN_FRAMING",
  NO_FRAMING = "NO_FRAMING",
  FRAMING = "FRAMING",
}

export interface FramingPlaneConstraint {
  readonly type: FramingPlaneConstraintType;
  readonly distanceFromStart: number;
  readonly width?: number; //if width is provided, then it means that constraint is covering that width
}
